import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { ROUTE_PATH } from '../../routes/paths';
import { Headeroptions } from './common/member-constants';

import { SectionLabel, Spinner, ToggleButton, UseFormDropDown } from '../../components/common';
import Header from '../../components/common/global-top-bar';
import SwitchBar from '../../components/switch-bar';
import RouteLeavingGuard from '../../components/route-leaving-prompt';
import HeadBack from './components/redirect';
import { requireMember } from './components/require-member';
import { Label } from '../peer-profiles/profile';
import { EditDetails } from './components/profile-edit';
import { useUser } from '../../hooks/useUsers';

import { useClientSummaryList } from '../../hooks/useClients';
import { MemberProfile, useMemberProfile } from '../../hooks/useMembers';
import { useAppSettings } from '../../hooks/useAppSettings';
import { TagGroup, useTagGroups } from '../../hooks/useTagGroups';
import { Tag } from '../../hooks/useTags';
import { useUpdateUser } from '../../hooks/useUsers';
import { useCreateRedeemPartnerCode } from '../../hooks/useRedeemPartnerCode';

import { paramsToObjects } from '../../components/common/create-params';
import { normalizeMobilePhone, validateMobilePhone } from '../../components/common/phone-validation';

const accountStatus = [
  { status: 'enabled', label: 'Enabled' },
  { status: 'disabled', label: 'Disabled' },
  { status: 'under_review', label: 'Under Review' },
  { status: 'suspended', label: 'Suspended' },
  { status: 'banned', label: 'Banned' },
];

const MembersProfile: React.FunctionComponent = (): JSX.Element => {
  const history = useHistory();
  const params = paramsToObjects();
  const callerRoleId = params['callerRoleId'];
  const formSubmit = useRef<any>(null);
  const { data: memberProfile, isLoading: isMemberProfileLoading } = useMemberProfile(callerRoleId);
  const { data: appSettings, isLoading: isAppSettingsLoading } = useAppSettings();
  const { data: tagGroups, isLoading: isTagGroupsLoading } = useTagGroups({ keys: 'PRONOUN,RACE/ETHNICITY,GENDER' });
  const { data: clients, isLoading: isClientsLoading } = useClientSummaryList({ show_all_clients: true });
  const { updateUser } = useUpdateUser();
  const { mutate: redeemPartnerCode } = useCreateRedeemPartnerCode();

  const [displayValues, setDisplayValues] = useState<MemberProfile | undefined>(memberProfile);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isCallerRoleStatus, setIsCallerRoleStatus] = useState<boolean>(false);
  const [isPaymentStatus, setIsPaymentStatus] = useState<boolean>(false);
  const [isTextCompatiblePhone, setIsTextCompatiblePhone] = useState<boolean>(true);
  const [language, setLanguage] = useState<string[]>([]);
  const [packages, setPackages] = useState<Array<{ name: string; code: string }>>([]);
  const [selectedPackage, setSelectedPackage] = useState<{ name: string; code: string }>();
  const [isSpinner, setIsSpinner] = useState<boolean>(true);
  const [isModified, setIsModified] = useState<boolean>(false);
  const [isOnloadMobileRequest, setIsOnloadMobileRequest] = useState<boolean>(true);
  const [isTestUser, setIsTestUser] = useState<boolean>(false);
  const [tags, setTags] = useState<any>({
    pronouns: [],
    race: [],
    genders: [],
  });

  const renewalDate = memberProfile
    ? memberProfile?.caller_role?.product_credit_subscriptions?.[0]?.renews_at.split('T')[0]
    : null;
  const { data: user } = useUser(memberProfile?.id);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      if (memberProfile) return memberProfile;
    }, [memberProfile]),
  });

  const selectLanguage = (data: any) => {
    if (language.includes(data)) {
      const filteredArray = language.filter((item: string) => item !== data);
      if (filteredArray.length >= 1) {
        setLanguage(filteredArray);
        setValue('caller_role.listener_preferences.languages', filteredArray);
      } else {
        setLanguage(['english']);
        setValue('caller_role.listener_preferences.languages', ['english']);
      }
    } else {
      setLanguage((languages: any) => [...languages, data]);
      setValue('caller_role.listener_preferences.languages', [...language, data]);
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzAbbr = new Date()
      .toLocaleTimeString('en-US', { timeZone: localTimeZone, timeZoneName: 'short' })
      .split(' ')[2];
    return `${date.toLocaleString('en-US', { timeZone: localTimeZone })} ${tzAbbr}`;
  };

  const handleContinueSave = (path: string) => {
    history.push(path);
  };

  const onSubmit = (data: any) => {
    const packageName = memberProfile?.caller_role?.active_subscription?.package.name;
    const packageCode = selectedPackage?.name !== packageName ? selectedPackage?.code : null;
    if (!data.mobile_phone || data.mobile_phone.trim() === '') {
      delete data.mobile_phone;
    }
    if (data.pronoun === '') {
      data.pronoun = null;
    }
    updateUser.mutate(
      {
        userId: data.id,
        data: {
          ...data,
        },
      },
      {
        onSuccess: () => {
          if (packageCode) {
            redeemPartnerCode(
              { userId: data.id, partnerCode: packageCode },
              {
                onSuccess: () => {
                  toast.success('Profile updated successfully');
                  setIsModified(false);
                },
              },
            );
          } else {
            toast.success('Profile updated successfully');
            setIsModified(false);
          }
        },
      },
    );
    setDisplayValues(data);
    setShowEditModal(false);
  };

  useEffect(() => {
    if (memberProfile) {
      setDisplayValues(memberProfile);

      if (clients && clients?.length > 0) {
        reset(memberProfile);
        const caller_role = memberProfile?.caller_role;
        setIsCallerRoleStatus(caller_role.status === 'enabled' ? true : false);
        setIsPaymentStatus(memberProfile?.caller_role?.stripe_payment_method_id ? true : false);
        setIsTextCompatiblePhone(!!memberProfile?.is_text_compatible_phone);
        setIsTestUser(memberProfile?.test_user);

        const currentClient =
          clients?.length && caller_role?.active_subscription
            ? clients.find((item: any) => item.name === caller_role?.active_subscription?.package?.client?.name)
            : null;
        const currentPlan =
          currentClient && currentClient?.packages?.length
            ? currentClient.packages.find((item: any) => item.name === caller_role?.active_subscription?.package?.name)
            : null;
        if (currentClient && currentClient?.packages?.length) {
          setPackages(currentClient.packages);
        }
        if (currentPlan) {
          setSelectedPackage(currentPlan);
          setValue('caller_role.active_subscription.package.name', currentPlan.name);
        }

        const language =
          caller_role?.listener_preferences?.languages?.length > 0
            ? caller_role?.listener_preferences?.languages
            : ['english'];
        setLanguage(language);
        const phone = memberProfile?.mobile_phone.replace('+1', '');
        if (isOnloadMobileRequest) {
          validateMobilePhone(phone);
          setIsOnloadMobileRequest(false);
        } else {
          const phoneNumber = normalizeMobilePhone(phone);
          setValue('mobile_phone', phoneNumber);
        }
        setIsSpinner(false);
        setIsModified(false);
      }
    }
  }, [memberProfile, clients, reset, setValue, isOnloadMobileRequest]);

  useEffect(() => {
    const getTags = (type: string) => {
      const tagGroup = tagGroups?.find((tag: TagGroup) => tag.name === type);
      return tagGroup?.tags?.map((tag: Tag) => tag.name) || [];
    };

    if (tagGroups && tagGroups?.length > 0) {
      const pronouns = getTags('Pronoun');
      const races = getTags('Race/Ethnicity');
      const genders = getTags('Gender');
      setTags({ pronouns, races, genders });
    }
  }, [tagGroups]);

  return (
    <div>
      {(isSpinner || isClientsLoading || isAppSettingsLoading || isMemberProfileLoading || isTagGroupsLoading) && (
        <Spinner />
      )}
      <Header heading={<HeadBack memberName={displayValues?.first_name} />} />
      <SwitchBar
        heading={Headeroptions}
        position={0}
        forwardParams
        optionalButton={
          <button
            id="form-submit"
            type="submit"
            form="member-profile-form"
            className={`${
              isModified ? 'stacked-bar-blue-400' : 'cursor-not-allowed bg-gray-300'
            } h-6 w-1/12 rounded-full px-3 text-center text-sm text-white`}
            disabled={!isModified}
            ref={formSubmit}
          >
            SAVE
          </button>
        }
      />
      <form onSubmit={handleSubmit(onSubmit)} id="member-profile-form">
        <div className="max-window-height-group flex justify-between overflow-y-auto">
          <div className="w-1/2">
            <div className="flex justify-between px-7 pt-4">
              <div className="text-gray-dark w-1/2 px-2 text-lg font-bold">
                {displayValues?.first_name} {displayValues?.last_name}
              </div>
              <div className="text-gray-dark w-1/2 px-2 text-lg font-bold">#{displayValues?.id}</div>
            </div>
            <div className="flex justify-end px-7 py-2">
              <div className="w-1/2 px-2">
                <button
                  data-testid="edit-details-button"
                  type="button"
                  onClick={() => setShowEditModal(true)}
                  className="bg-blue-primary flex h-8 w-24 items-center justify-center rounded-sm px-2 text-sm text-white"
                >
                  Edit Details
                </button>
              </div>
            </div>
            <div className="px-7">
              {user && (
                <>
                  <SectionLabel Label="Roles" />
                  <div className="mt-6">
                    {user?.administrator_role !== null && (
                      <p className="stacked-bar-red-primary my-1 w-full max-w-[256px] rounded-full px-2 py-1 text-center text-sm text-white">
                        Admin
                      </p>
                    )}
                    {user?.caller_role !== null && (
                      <p className="stacked-bar-green-primary my-1 w-full max-w-[256px] rounded-full px-2 py-1 text-center text-sm text-white">
                        Caller
                      </p>
                    )}
                    {user?.client_administrator_role !== null && (
                      <p className="stacked-bar-orange-primary my-1 w-full max-w-[256px] rounded-full px-2 py-1 text-center text-sm text-white">
                        Client Admin
                      </p>
                    )}
                    {user?.listener_role !== null && (
                      <p className="stacked-bar-blue-100 my-1 w-full max-w-[256px] rounded-full px-2 py-1 text-center text-sm text-white">
                        Listener
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="px-7">
              <SectionLabel Label={'Plan Details'} />
            </div>
            <div className="flex justify-between px-7 py-2">
              <div className="w-full px-2">
                <Label
                  Label="Group"
                  value={displayValues?.caller_role?.active_subscription?.package?.client?.name || 'N/A'}
                  dataTestId="group"
                />
              </div>
              <div className="w-full px-2">
                <Label
                  Label="Plan"
                  value={displayValues?.caller_role?.active_subscription?.package?.name || 'N/A'}
                  dataTestId="plan"
                />
              </div>
            </div>
            <div className="flex justify-between px-7 py-2">
              <div className="px-2">
                <Label
                  Label="Minutes Remaining"
                  value={memberProfile?.user_subscription?.remaining_minutes || 'N/A'}
                  dataTestId="minutes-remaining"
                />
              </div>
              {renewalDate && (
                <div className="w-full px-2">
                  <Label Label="Plan Renewal Date" value={renewalDate} dataTestId="renewal-date" />
                </div>
              )}
            </div>
            <div className="px-7">
              <SectionLabel Label={'Contact'} />
            </div>
            <div className="flex justify-between px-7 py-2">
              <div className="w-full px-2">
                <Label Label="Email" value={displayValues?.email_address || 'N/A'} dataTestId="email" />
              </div>
            </div>
            <div className="flex justify-between px-7 py-2">
              <div className="w-full px-2">
                <Label
                  Label="Eligibility Email"
                  value={displayValues?.eligibility_email || 'N/A'}
                  dataTestId="eligibility-email"
                />
              </div>
            </div>
            <div className="flex justify-between px-7 py-2">
              <div className="w-1/2 px-2">
                <Label Label="Phone" value={displayValues?.mobile_phone || 'N/A'} dataTestId="phone" />
              </div>
              <div className="w-1/2 px-2">
                <Label Label="Time Zone" value={displayValues?.timezone || 'N/A'} dataTestId="timezone" />
              </div>
            </div>
            <div className="flex justify-between px-7 py-2">
              <div className="w-full px-2">
                <Label
                  Label="Receive SMS Text Comms"
                  value={displayValues?.is_text_compatible_phone ? 'Yes' : 'No'}
                  dataTestId="sms-comms"
                />
              </div>
            </div>
            <div className="px-7">
              <SectionLabel Label={'Matching Demographics'} />
            </div>
            <div className="flex justify-between px-7 py-2">
              <div className="w-1/2 px-2">
                <Label Label="DOB (Age)" value={displayValues?.date_of_birth || 'N/A'} dataTestId="dob" />
              </div>
              <div className="w-1/2 px-2">
                <Label Label="Gender" value={displayValues?.gender || 'N/A'} dataTestId="gender" />
              </div>
            </div>
            <div className="flex justify-between px-7 py-2">
              <div className="w-1/2 px-2">
                <Label Label="Pronouns" value={displayValues?.pronoun || 'N/A'} dataTestId="pronouns" />
              </div>
              <div className="w-1/2 px-2">
                <Label Label="Race" value={displayValues?.race || 'N/A'} dataTestId="race" />
              </div>
            </div>
            <div className="flex justify-between px-7 py-2">
              <div className="w-full px-2">
                <Label
                  Label="Preferred Language"
                  value={
                    displayValues?.caller_role?.listener_preferences?.languages &&
                    displayValues?.caller_role?.listener_preferences?.languages?.length > 0
                      ? displayValues?.caller_role?.listener_preferences?.languages
                          .map((lang: string) => lang.charAt(0).toUpperCase() + lang.slice(1))
                          .join(', ')
                      : 'N/A'
                  }
                  dataTestId="preferred-language"
                />
              </div>
            </div>
            <div className="flex flex-col justify-between px-7 py-2">
              <label className="input-label">Internal Notes</label>
              <textarea
                {...register('caller_role.notes', {
                  onBlur: (e: any) => setValue('caller_role.notes', e.target.value.trim()),
                  onChange: () => setIsModified(true),
                })}
                className="text-gray-dark bg-gray-background-light h-28 w-full rounded-md px-3 py-2 text-sm font-extrabold leading-4 focus:outline-none"
              />
            </div>
          </div>
          <div className="w-1/2">
            <div className="px-7">
              <SectionLabel Label={'Status'} />
            </div>
            <div className="flex justify-between px-7 py-2">
              <div className="w-1/2 px-2">
                <div className="">
                  <UseFormDropDown label={'User Account'}>
                    <select
                      className="select-box w-full focus:ring-transparent"
                      {...register('status', {
                        onChange: () => setIsModified(true),
                      })}
                    >
                      {accountStatus.length > 0 &&
                        accountStatus.map((item) => (
                          <option key={item.status} value={item.status}>
                            {item.label}
                          </option>
                        ))}
                    </select>
                  </UseFormDropDown>
                </div>
              </div>
              <div className="w-1/2 px-2 pt-6">
                <div className="flex items-center">
                  <label className="text-gray-dark block pr-4 leading-4"> Test User </label>
                  <ToggleButton
                    id={'TestUser'}
                    status={isTestUser}
                    setStatus={(data) => {
                      setIsTestUser(data);
                      const status = data ? true : false;
                      setValue('test_user', status);
                      setIsModified(true);
                    }}
                    initialstatus={true}
                  ></ToggleButton>
                </div>
              </div>
            </div>
            <div className="flex justify-between px-7 py-2">
              <div className="w-1/2 px-2">
                <div className="flex items-center">
                  <label className="text-gray-dark block pr-4 leading-4"> Caller Role </label>
                  <ToggleButton
                    id={'CallerRole'}
                    status={isCallerRoleStatus}
                    setStatus={(data) => {
                      setIsCallerRoleStatus(data);
                      const status = data ? 'enabled' : 'disabled';
                      setValue('caller_role.status', status);
                      setIsModified(true);
                    }}
                    initialstatus={true}
                  ></ToggleButton>
                </div>
              </div>
              <div className="w-1/2 px-2">
                <div className="flex items-center">
                  <label className="text-gray-dark block pr-4 leading-4"> Payment Setup </label>
                  <ToggleButton
                    id={'Paymentsetup'}
                    status={isPaymentStatus}
                    setStatus={(data) => {}}
                    disabled={true}
                    initialstatus={true}
                  ></ToggleButton>
                </div>
              </div>
            </div>
            <div className="px-7">
              <SectionLabel Label={'Subscription Details'} />
            </div>
            <div className="flex justify-between px-7 py-2">
              <div className="w-full px-2">
                <Label
                  Label="Eligible as of"
                  value={formatDateTime(memberProfile?.caller_role?.active_subscription?.created_at || '')}
                  dataTestId="group"
                />
              </div>
              <div className="w-full px-2">
                <Label
                  Label="Activated"
                  value={formatDateTime(memberProfile?.caller_role?.onboarding_completed || '')}
                  dataTestId="group"
                />
              </div>
            </div>
          </div>
        </div>
        <EditDetails
          showEditModal={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleSubmit(onSubmit)}
          tags={tags}
          timeZones={appSettings?.supported_timezones || []}
          clients={clients || []}
          packages={packages || []}
          language={language}
          selectLanguage={selectLanguage}
          setPackages={setPackages}
          setSelectedPackage={setSelectedPackage}
          setIsModified={setIsModified}
          register={register}
          getValues={getValues}
          setValue={setValue}
          errors={errors}
          isTextCompatiblePhone={isTextCompatiblePhone}
          setIsTextCompatiblePhone={setIsTextCompatiblePhone}
        />
      </form>
      <RouteLeavingGuard
        when={isModified}
        navigate={(path: string) => history.push(path)}
        shouldBlockNavigation={(location: any) => {
          if (location.pathname !== ROUTE_PATH.MEMBERS_PROFILE) {
            return true;
          }
          return false;
        }}
        titleText={'Alert'}
        contentText={'You have unsaved changes. Do you want to leave this page and lose your changes?'}
        cancelButtonText={'Cancel'}
        confirmButtonText={'Disregard changes'}
        confirmSaveButtonText="Continue and Save changes"
        handleContinueSaveChange={(path) => {
          formSubmit?.current.click();
          setIsModified(false);
          setTimeout(() => {
            handleContinueSave(path);
          }, 100);
        }}
      />
    </div>
  );
};

export default requireMember(MembersProfile);
